import { z } from "zod";
import {
  PaginationParamsSchema,
  SuccessMetaSchema,
  PaginationMetaSchema,
} from "../common";
import {
  PricingLogicSchema,
  ModalDisplaySchema,
  QuantityTiersDisplaySchema,
  BannerDisplaySchema,
  OneTickDisplaySchema,
  ProductTargetBaseSchema,
  BundleDisplaySchema,
} from "../upsell-constructs";

// =========================================================================
// SHARED SUB-SCHEMAS
// =========================================================================

// 1. PRODUCT TARGET
// Define qué producto o bundle se está vendiendo.
export const OfferTargetSchema = ProductTargetBaseSchema.refine(
  (data) => {
    // `synthetic` is the explicit "no Shopify resource attached" target —
    // used by 1-tick upsells where the merchant sells an ad-hoc item
    // (custom fee, manually-fulfilled add-on, etc.) without linking to a
    // product. Any other missing/unknown type is rejected so phantom
    // targets can't slip through into the cart pipeline.
    if (data.type === "synthetic") return true;
    if (data.type === "shopify_variant") return !!data.shopify_variant_id;
    if (data.type === "shopify_product") return !!data.shopify_product_id;
    if (data.type === "bundle") return !!data.bundle_id;
    return false;
  },
  {
    message:
      "Debe proveer un type válido (synthetic, shopify_product, shopify_variant, bundle) y el id correspondiente",
    path: ["type"],
  }
);

// 2. TRIGGER RULES
// Define cuándo se debe disparar esta oferta.
export const TriggerRulesSchema = z.object({
  rule_type: z.enum([
    "all", // Siempre visible
    "specific_products", // Si el carrito contiene X
    "cart_value_range", // Entre min y max total
  ]),
  // IDs de referencia para las reglas
  product_ids: z.array(z.string()).optional(),
  // Optional display metadata for `specific_products` triggers — keyed
  // by `id` (kept independent of position in `product_ids`). Lets the
  // admin editor render the picked products' titles/images on reload
  // without re-opening the resource picker. The BE itself only uses
  // `product_ids` for cart filtering; this field is round-trip storage
  // for admin UI.
  product_meta: z
    .array(
      z.object({
        id: z.string(),
        title: z.string().optional(),
        image_url: z.string().optional(),
      })
    )
    .optional(),
  min_cart_value: z.number().optional(),
  max_cart_value: z.number().optional(),
});

// 3. CONFIGURACIÓN POLIMÓRFICA
// Une el Display Type + Display Data + Pricing Logic de forma coherente.

// Caso A: Pricing Logic a nivel raíz
// Requieren una lógica de precios a nivel raíz (ej: "20% OFF en este producto")
const ModalConfigSchema = z.object({
  display_type: z.literal("modal"),
  placement: z
    .enum(["before_complete", "after_complete", "on_close"])
    .default("after_complete"),
  display_data: ModalDisplaySchema,
  pricing_logic: PricingLogicSchema,
});

const BannerConfigSchema = z.object({
  display_type: z.literal("banner"),
  display_data: BannerDisplaySchema,
  pricing_logic: PricingLogicSchema,
});

const OneTickConfigSchema = z.object({
  display_type: z.literal("one_tick"),
  display_data: OneTickDisplaySchema,
  pricing_logic: PricingLogicSchema,
});

const BundleOfferConfigSchema = z.object({
  display_type: z.literal("bundle"),
  display_data: BundleDisplaySchema,
  pricing_logic: PricingLogicSchema,
});

// Caso B: Quantity Tiers
// La lógica de precios vive DENTRO de los tiers en `display_data`.
// A nivel raíz, el pricing_logic se ignora o es 'none'.
const QuantityTiersConfigSchema = z.object({
  display_type: z.literal("quantity_tiers"),
  display_data: QuantityTiersDisplaySchema,
  pricing_logic: z
    .object({ action: z.literal("none") })
    .optional()
    .default({ action: "none" }),
});

export const OfferConfigSchema = z.discriminatedUnion("display_type", [
  ModalConfigSchema,
  BannerConfigSchema,
  QuantityTiersConfigSchema,
  OneTickConfigSchema,
  BundleOfferConfigSchema,
]);

// =========================================================================
// 1. GET /admin/offers (LIST)
// =========================================================================

export const GetOffersQuerySchema = PaginationParamsSchema.extend({
  search: z.string().optional(),
  is_active: z
    .enum(["true", "false"])
    .transform((val) => val === "true")
    .optional(),
  // Merchant-facing category. When present, the BE translates it to a
  // `(displayType, placement)` filter via `KIND_TO_DB_FILTER`. Prefer
  // this over `display_type`/`placement` for list pages — those two stay
  // for other callers (widget bootstrap, etc.).
  kind: z
    .enum(["one_tick", "one_click", "downsell", "bundle", "quantity_tiers"])
    .optional(),
  display_type: z
    .enum(["modal", "quantity_tiers", "banner", "one_tick", "bundle"])
    .optional(),
  placement: z
    .enum(["before_complete", "after_complete", "on_close"])
    .optional(),
});

export const OfferSummarySchema = z.object({
  id: z.string(),
  internal_name: z.string(),
  display_type: z.enum([
    "modal",
    "quantity_tiers",
    "banner",
    "one_tick",
    "bundle",
  ]),
  placement: z
    .enum(["before_complete", "after_complete", "on_close"])
    .optional(),
  offer_category: z.enum(["upsell", "downsell"]),
  is_active: z.boolean(),
  priority: z.number().int(),
  target_summary: z.string().optional(),
  stats: z.object({
    times_shown: z.number().int().default(0),
    times_accepted: z.number().int().default(0),
    conversion_rate: z.number().min(0).max(100).default(0),
  }),
  updated_at: z.iso.datetime(),
});

export const GetOffersResponseSchema = z.object({
  data: z.array(OfferSummarySchema),
  meta: SuccessMetaSchema.extend({
    ...PaginationMetaSchema.shape,
    modal_active_count: z.number().int(),
    modal_active_limit: z.number().int(),
    downsell_active_count: z.number().int(),
    /** null = no cap configured for this shop (unlimited) */
    downsell_active_limit: z.number().int().nullable(),
  }),
});

// =========================================================================
// 2. GET /admin/offers/:id (DETAIL)
// =========================================================================

export const OfferDetailResponseSchema = z.object({
  id: z.string(),
  shop_id: z.string(),
  internal_name: z.string(),
  is_active: z.boolean(),
  priority: z.number().int(),
  offer_category: z.enum(["upsell", "downsell"]),

  // Configuración compuesta (Polimórfica)
  config: OfferConfigSchema,

  // Objetivo (Qué vendemos)
  target: OfferTargetSchema,

  // Disparadores (Cuándo mostramos)
  triggers: TriggerRulesSchema,

  // Estadísticas (Lectura)
  stats: z.object({
    times_shown: z.number().int(),
    times_accepted: z.number().int(),
  }),

  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

// =========================================================================
// 3. POST /admin/offers (CREATE)
// =========================================================================

export const CreateOfferRequestSchema = z.object({
  internal_name: z.string().min(1, "El nombre interno es requerido"),
  is_active: z.boolean().default(true),
  priority: z.number().int().default(0),

  config: OfferConfigSchema,

  target: OfferTargetSchema,
  triggers: TriggerRulesSchema,
});

// =========================================================================
// 4. PUT /admin/offers/:id (UPDATE)
// =========================================================================

export const UpdateOfferRequestSchema = z.object({
  internal_name: z.string().min(1).optional(),
  is_active: z.boolean().optional(),
  priority: z.number().int().optional(),

  config: OfferConfigSchema.optional(),

  target: OfferTargetSchema.optional(),
  triggers: TriggerRulesSchema.optional(),
});

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type OfferTarget = z.infer<typeof OfferTargetSchema>;
export type TriggerRules = z.infer<typeof TriggerRulesSchema>;
export type OfferConfig = z.infer<typeof OfferConfigSchema>;

export type GetOffersQuery = z.infer<typeof GetOffersQuerySchema>;
export type GetOffersResponse = z.infer<typeof GetOffersResponseSchema>;
export type OfferDetailResponse = z.infer<typeof OfferDetailResponseSchema>;
export type CreateOfferRequest = z.infer<typeof CreateOfferRequestSchema>;
export type UpdateOfferRequest = z.infer<typeof UpdateOfferRequestSchema>;
